import Moment from "react-moment";
import * as CommonUtilities from "../../../common/utils/CommonUtils.js";

/* =============================================================================
   One open order.
   -----------------------------------------------------------------------------
   This is the screen a customer sits and stares at, refreshing, while their
   food is made. It used to be a 0–100% bar.

   ⚠ The switch below is UNCHANGED — same seven branches, same percentages, same
   wording, same inProgress flag. Only two things were added, and neither alters
   an existing value:

     · stepIndex — derived from progressBarAmount, so the step tracker and the
       old percentage can never disagree.
     · isStopped — set ONLY in cases 0 and 5. Those two are terminal failures
       (POS rejected / refunded), and under the old bar they rendered as "0%",
       which reads as "nothing has happened yet" rather than "this is over and
       you have your money back". They now get their own panel instead of a
       tracker sitting at zero.

   ⚠ Catering (PickupMenuType === 'webordering') deliberately shows NO progress
   section — a catering order is not made to order while you wait. That is the
   !isCateringMenu guard, and it must stay.

   ⚠ Get Directions is hidden for delivery: the customer is not going anywhere.

   ⚠ The phone formatter has three regex branches plus a fallback, because the
   API returns mobiles, 10-digit landlines and 8-digit landlines unformatted.
   Left exactly as it was.
   ============================================================================= */

const STEPS = ["Placed", "Accepted", "Preparing", "Ready", "Collected"];

export default function Order(props) {

    const warningText = "#BE6717";
    const darkText = "#212529";

    let isWebOrder;
    let isCateringMenu = CommonUtilities.StaticFields.PickupMenuType === 'webordering';
    if (isCateringMenu) {
        isWebOrder = true;
    } else {
        isWebOrder = props.urlConfig.Type === '' || props.urlConfig.Type === null || props.urlConfig.Type === 'null' || props.urlConfig.Type === 'web' ? true : false;
    }
    const isDelivery = props.urlConfig.MenuType === 'delivery';

    let progressBarAmount = 0;
    let progressText = "Waiting for the store to accept your order.";
    let progressTextColor = darkText;
    let inProgress = true;
    let isStopped = false;

    switch(props.order.orderStatusID){
        case 0:
            // POS Rejected
            progressBarAmount = 0;
            progressText = "Your order has not been accepted by the store. Any payment made has been refunded.";
            progressTextColor = warningText;
            inProgress = false;
            isStopped = true;
            break;
        case 1:
            // POS Accepted
            progressBarAmount = 25;
            progressText = "Your order has been accepted!";
            inProgress = false;
            break;
        case 2:
            // Preparing
            progressBarAmount = 50;
            progressText = "Your order is being prepared.";
            inProgress = false;
            break;
        case 3:
            // Ready
            progressBarAmount = 75;
            progressText = "Your order is ready for pick up!";
            inProgress = false;
            break;
        case 4:
            // Collected
            progressBarAmount = 100;
            progressText = "Your order has been collected!";
            inProgress = false;
            break;
        case 5:
            // Refund
            progressBarAmount = 0;
            progressText = "Your order has not been accepted by the store. Any payment made has been refunded.";
            progressTextColor = warningText;
            inProgress = false;
            isStopped = true;
            break;
        case 6:
            // Send To POS
            progressBarAmount = 0;
            progressText = "Waiting for the store to accept your order.";
            inProgress = true;
            break;
        default:
            // Fallback
            progressBarAmount = 0;
            progressText = "Waiting for the store to accept your order.";
            inProgress = true;
    }

    // 0 / 25 / 50 / 75 / 100  ->  0 / 1 / 2 / 3 / 4
    const stepIndex = Math.round(progressBarAmount / 25);

    let originalBusinessPhone = props.order.businessPhone;
    let amendedBusinessPhone = "";
    if (CommonUtilities.RegexPatterns.regexMobile.test(originalBusinessPhone)) {
        // Mobile - 10 Digits
        amendedBusinessPhone = `${originalBusinessPhone.slice(0, 4)} ${originalBusinessPhone.slice(4, 7)} ${originalBusinessPhone.slice(7, 10)}`;
    } else if (CommonUtilities.RegexPatterns.regexLandline10Char.test(originalBusinessPhone)) {
        // Landline - 10 Digits
        amendedBusinessPhone = `${originalBusinessPhone.slice(0, 2)} ${originalBusinessPhone.slice(2, 6)} ${originalBusinessPhone.slice(6, 10)}`;
    } else if (CommonUtilities.RegexPatterns.regexLandline8Char.test(originalBusinessPhone)) {
        // Landline - 8 Digits
        amendedBusinessPhone = `${originalBusinessPhone.slice(0, 4)} ${originalBusinessPhone.slice(4, 8)}`;
    } else {
        // Fallback
        amendedBusinessPhone = originalBusinessPhone;
    }

    const tracker =
        <div className="wo-track">
            <ol className="wo-track__steps">
                {STEPS.map((label, i) => {
                    const state = i < stepIndex ? 'done' : i === stepIndex ? 'now' : 'todo';
                    return (
                        <li key={label} className={`wo-track__step wo-track__step--${state}`}>
                            <span className="wo-track__dot" aria-hidden="true">
                                {state === 'done' &&
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M5 12.5l4.5 4.5L19 7.5" />
                                    </svg>}
                            </span>
                            <span className="wo-track__label">{label}</span>
                        </li>
                    );
                })}
            </ol>
            <p className="wo-track__text" style={{color: progressTextColor}}>
                {inProgress && <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>}
                {progressText}
            </p>
        </div>;

    const stopped =
        <div className="wo-track wo-track--stopped">
            <span className="wo-track__stopMark" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2.4" strokeLinecap="round">
                    <path d="M12 7.5v6M12 16.8v.2" />
                    <circle cx="12" cy="12" r="9" />
                </svg>
            </span>
            <p className="wo-track__stopHead">Order not accepted</p>
            <p className="wo-track__text" style={{color: progressTextColor}}>{progressText}</p>
        </div>;

    return (
        <div className="wo-ord">
            <div className="wo-ord__head">
                <span className="wo-ord__num">{`Order #${props.order.cartSummaryRecId}`}</span>
                {isWebOrder &&
                    <span className="wo-ord__when">
                        Placed <Moment parse="YYYY-MM-DDTHH:mm:ss" format="h:mma">{props.order.paymentDate}</Moment>
                        {' · '}
                        <Moment parse="YYYY-MM-DDTHH:mm:ss" format="DD/MM/YYYY">{props.order.paymentDate}</Moment>
                    </span>}
                <span className="wo-ord__emailed">Your order confirmation has been emailed to you</span>
            </div>

            {isWebOrder &&
                <>
                    {!isCateringMenu && (isStopped ? stopped : tracker)}

                    <div className="wo-ord__store">
                        {isDelivery && <span className="wo-ord__storeLabel">Delivering from</span>}
                        <div className="wo-ord__storeRow">
                            <span className="wo-ord__storeAddr">
                                <strong>{props.order.locationName}</strong>
                                {props.order.businessAddressLine1 && <><br />{props.order.businessAddressLine1}</>}
                                {props.order.businessAddressLine2 && <><br />{props.order.businessAddressLine2}</>}
                                <br />{props.order?.businessSuburb && props.order.businessSuburb} {props.order.businessStateName} {props.order.businessPostCode}
                            </span>
                            {props.order.businessPhone &&
                                <span className="wo-ord__storePhone">
                                    <span className="wo-ord__storeLabel">Contact store</span>
                                    <a href={`tel:${props.order.businessPhone}`}>{amendedBusinessPhone}</a>
                                </span>}
                        </div>
                    </div>

                    {!isDelivery &&
                        <a className="wo-ord__dir" href={`https://www.google.com/maps/dir//${props.order.gpsLatitude},${props.order.gpsLongitude}`} target="_blank" rel="noreferrer">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z" />
                                <circle cx="12" cy="10" r="2.6" />
                            </svg>
                            Get directions
                        </a>}
                </>
            }
        </div>
    )
}
